import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, SendHorizonal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full font-courier text-sm bg-void border-2 border-input text-paper px-4 py-3 focus:outline-none focus:border-crimson focus:ring-1 focus:ring-crimson placeholder:text-paper/30 transition-colors duration-150";

export default function Forms() {
  const [nlEmail, setNlEmail] = useState("");
  const [nlBusy, setNlBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [cBusy, setCBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setNlBusy(true);
    try {
      const r = await axios.post(`${API}/newsletter`, { email: nlEmail });
      toast.success(
        r.data.status === "already_subscribed"
          ? "You're already sworn to the coven."
          : "The pact is sealed. Watch your inbox."
      );
      setNlEmail("");
    } catch {
      toast.error("That email looks cursed. Try again.");
    } finally {
      setNlBusy(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    setCBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your message reached the crypt. We'll answer from the shadows.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("The ritual needs every field. Try again.");
    } finally {
      setCBusy(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 sm:py-36 bg-pulp/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="border border-blooddeep/50 bg-pulp p-8 sm:p-12"
        >
          <span className="inline-block bg-blooddeep text-white font-marker text-xs px-3 py-1 rotate-2 mb-6 ink-shadow-sm">
            NO SPAM. ONLY DAMNATION.
          </span>
          <h2 className="font-display text-2xl sm:text-3xl uppercase text-paper mb-4">
            JOIN THE <span className="text-crimson">COVEN</span>
          </h2>
          <p className="font-type text-sm text-paper/60 leading-relaxed mb-8">
            Swear your email to the pact: new releases, forbidden variant covers, and
            subscriber-only discounts from the deep dark.
          </p>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={nlEmail}
              onChange={(e) => setNlEmail(e.target.value)}
              placeholder="your@email.com"
              className={`${inputCls} flex-1 py-3.5`}
            />
            <button
              data-testid="newsletter-submit-button"
              disabled={nlBusy}
              className="flex items-center justify-center gap-2 font-impact uppercase tracking-wider bg-blooddeep text-bone border-2 border-black px-6 py-3.5 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson hover:-translate-y-0.5 active:translate-x-0.5 active:translate-y-0.5 disabled:opacity-50"
            >
              <Mail size={16} /> {nlBusy ? "Binding..." : "Sign in Blood"}
            </button>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="border border-blooddeep/50 bg-pulp p-8 sm:p-12"
        >
          <span className="inline-block bg-blooddeep text-white font-marker text-xs px-3 py-1 -rotate-2 mb-6 ink-shadow-sm">
            PITCHES, FAN ART, WHOLESALE SOULS
          </span>
          <h2 className="font-display text-2xl sm:text-3xl uppercase text-paper mb-4">
            SUMMON <span className="text-crimson">THE PRESS</span>
          </h2>
          <p className="font-type text-sm text-paper/60 leading-relaxed mb-8">
            A pitch, a curse, fan art, or wholesale inquiries — whisper them into the dark.
            We answer.
          </p>
          <form onSubmit={sendMessage} className="space-y-4">
            <input
              data-testid="contact-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className={inputCls}
            />
            <input
              data-testid="contact-email-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Your email"
              className={inputCls}
            />
            <textarea
              data-testid="contact-message-input"
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Whisper it into the dark. We're listening."
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="contact-submit-button"
              disabled={cBusy}
              className="flex items-center gap-2 font-impact uppercase tracking-wider bg-blooddeep text-bone border-2 border-black px-7 py-4 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1 disabled:opacity-50"
            >
              <SendHorizonal size={16} /> {cBusy ? "Summoning..." : "Summon Us"}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
